import type { EvidenceFact } from "@digital-twins/shared-types";

/**
 * Shared numbering seam for analyst fact citations.
 *
 * The inline chips in `SafeAnalystMarkdown` and the rows in `AnalystEvidence`
 * must agree on every number, so neither computes its own. `AnalystMessage`
 * builds this once per message and hands the same object to both.
 *
 * The marker written into answer text is the fact id in brackets — the model is
 * told to cite `[fact-id]` (see `prompts.py`), and `_fact_id` in the python
 * service produces ids that already carry the `fact-` prefix. So the text
 * between the brackets IS the `factId`, with no further parsing.
 */

/** Matches one `[fact-…]` marker; group 1 is the bare factId. */
export const FACT_CITATION_RE = /\[(fact-[A-Za-z0-9._:-]+)\]/g;

/**
 * A marker plus the whitespace in front of it. Used when a marker is removed
 * rather than rendered, so a stripped citation does not leave a double space
 * before the sentence's full stop.
 */
export const FACT_CITATION_WITH_LEADING_SPACE_RE =
  /\s*\[fact-[A-Za-z0-9._:-]+\]/g;

/**
 * Caveats are rendered under `### Keep in mind` and then stripped from the
 * visible answer. Numbering must ignore that section too, or a caveat-only
 * citation would take a source number that has no chip.
 */
const KEEP_IN_MIND_SECTION_RE =
  /(?:^|\n)#{1,6}\s*Keep in mind\s*\n[\s\S]*?(?=\n#{1,6}\s|\s*$)/i;

export function stripKeepInMindSection(content: string): string {
  return content.replace(KEEP_IN_MIND_SECTION_RE, "\n").trim();
}

/**
 * One cited breakdown is one source.
 *
 * Crosstab cells already share `custom-analysis-{run}`. Demographic buckets
 * are stored as `respondent-attribute.{column}.{value}`; those values are one
 * attribute. Other facts stay on their own source id.
 */
const RESPONDENT_ATTRIBUTE_VALUE = /^respondent-attribute\.([^.]+)\..+/;

export function evidenceSourceKey(fact: EvidenceFact): string {
  const sourceId = fact.source.sourceId;
  const attribute = RESPONDENT_ATTRIBUTE_VALUE.exec(sourceId);
  if (attribute) {
    return `respondent-attribute.${attribute[1]}`;
  }
  return sourceId;
}

export function evidenceGroupTitle(facts: Array<EvidenceFact>): string {
  const sourceLabels = new Set(facts.map((fact) => fact.source.label));
  if (sourceLabels.size === 1) {
    return facts[0].source.label;
  }
  const heads = facts.map((fact) => {
    const separator = fact.label.indexOf(": ");
    return separator === -1 ? fact.label : fact.label.slice(0, separator);
  });
  if (new Set(heads).size === 1) {
    return heads[0];
  }
  return facts[0].source.label;
}

export interface FactNumbering {
  /**
   * factId to its 1-based display number. Only facts cited in the visible
   * answer (and present in evidence) are numbered, in first-appearance order.
   * A chip and its sources-list row both read this map.
   */
  numberByFactId: Map<string, number>;
  /** Resolvable factIds cited in the visible text, in first-appearance order. */
  placedFactIds: Array<string>;
  /** factIds present in evidence but never cited in the visible text. */
  unplacedFactIds: Array<string>;
}

/**
 * Build the numbering for one message.
 *
 * Two rules carry the behavior the plan pins down:
 *
 * 1. A factId cited more than once reuses its first number. The model routinely
 *    cites the same fact in a headline and again in a bullet.
 * 2. A cited id with no matching evidence entry is left OUT of the map entirely,
 *    which is the signal to callers that the marker must be stripped rather than
 *    rendered. Unresolvable ids genuinely reach the client: `audit.py` filters
 *    cited ids against known facts and `normalize_evidence_citations` only
 *    resolves truncations that are unique, so a hallucinated or ambiguous id
 *    survives into the answer text. Rendering a chip that points at nothing
 *    would turn today's silent strip into a visible defect.
 */
export function buildFactNumbering(
  content: string,
  evidence: Array<EvidenceFact>,
): FactNumbering {
  const knownFactIds = new Set(evidence.map((fact) => fact.factId));
  const visibleContent = stripKeepInMindSection(content);

  const placedFactIds: Array<string> = [];
  const seen = new Set<string>();

  // `matchAll` needs the regex to stay global; a shared literal would carry
  // `lastIndex` between calls, so the pattern is rebuilt per invocation.
  const matches = visibleContent.matchAll(
    new RegExp(FACT_CITATION_RE.source, "g"),
  );
  for (const match of matches) {
    const factId = match[1];
    if (!knownFactIds.has(factId) || seen.has(factId)) {
      continue;
    }
    seen.add(factId);
    placedFactIds.push(factId);
  }

  // Deduped: the same `factId` can appear twice in `evidence` (two tool
  // results describing one underlying fact).
  const unplacedFactIds: Array<string> = [];
  const unplacedSeen = new Set<string>();
  for (const fact of evidence) {
    if (seen.has(fact.factId) || unplacedSeen.has(fact.factId)) {
      continue;
    }
    unplacedSeen.add(fact.factId);
    unplacedFactIds.push(fact.factId);
  }

  // Facts from one breakdown share a number. A chip and its sources row both
  // read this map, so a six-bucket age split is source 4, not sources 4–9.
  const sourceKeyByFactId = new Map(
    evidence.map((fact) => [fact.factId, evidenceSourceKey(fact)]),
  );
  const numberBySourceKey = new Map<string, number>();
  const numberByFactId = new Map<string, number>();
  for (const factId of placedFactIds) {
    const sourceKey = sourceKeyByFactId.get(factId) ?? factId;
    const existing = numberBySourceKey.get(sourceKey);
    if (existing !== undefined) {
      numberByFactId.set(factId, existing);
      continue;
    }
    const number = numberBySourceKey.size + 1;
    numberBySourceKey.set(sourceKey, number);
    numberByFactId.set(factId, number);
  }

  return { numberByFactId, placedFactIds, unplacedFactIds };
}

/**
 * Whether a cited marker should render a chip. False means the marker is
 * unresolvable and the caller strips it, exactly as the tab has always done.
 */
export function isRenderableFactId(
  factId: string,
  numbering: FactNumbering,
): boolean {
  return numbering.numberByFactId.has(factId);
}
